import React, { useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { Loader2, ShieldCheck, Home, FileText, Gift, BellRing, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)] transition-shadow";

const PERKS = [
  { icon: Home, title: "Your properties, saved", text: "Store each home once — request services in a tap." },
  { icon: FileText, title: "Everything in one place", text: "Quotes, bookings, payment status & completion reports." },
  { icon: Gift, title: "Ascendia Rewards", text: "Earn points on every completed service and referral." },
  { icon: BellRing, title: "Real-time updates", text: "Know the moment your quote is ready or a deposit lands." },
];

export default function AccountAuth() {
  const nav = useNavigate();
  const site = useSite();
  const [params] = useSearchParams();
  const location = useLocation();
  const isSignupRoute = location.pathname.endsWith("/signup") || params.get("mode") === "signup";
  const next = params.get("next") || "/account";
  const prefillEmail = params.get("email") || "";
  const prefillName = (params.get("name") || "").trim();
  const [pf, pl] = prefillEmail ? [prefillName.split(" ")[0] || "", prefillName.split(" ").slice(1).join(" ") || ""] : ["", ""];
  const [mode, setMode] = useState(prefillEmail || isSignupRoute ? "signup" : "login"); // login | signup | forgot
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ first_name: pf, last_name: pl, email: prefillEmail, phone: params.get("phone") || "", password: "", confirm_password: "", city: "", referral_code: "", identifier: prefillEmail });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        if (!f.first_name || !f.last_name || !f.email || !f.phone || !f.password) { toast.error("Please complete all required fields."); setBusy(false); return; }
        if (f.password !== f.confirm_password) { toast.error("Passwords do not match."); setBusy(false); return; }
        await api.post("/client/signup", f);
        toast.success("Welcome to Ascendia");
        window.dispatchEvent(new Event("ascendia-auth-change"));
        nav(next);
      } else if (mode === "login") {
        await api.post("/client/login", { identifier: f.identifier || f.email, password: f.password });
        toast.success("Welcome back");
        window.dispatchEvent(new Event("ascendia-auth-change"));
        nav(next);
      } else {
        await api.post("/client/forgot-password", { email: f.email, origin: window.location.origin });
        toast.success("If that email exists, a reset link is on its way.");
        setMode("login");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <>
      <SEO title={mode === "signup" ? "Create your Ascendia account" : "Client Login"} description="Access your private Ascendia client area." noindex />
      <div className="min-h-screen bg-[#0B0B0C] text-white grid lg:grid-cols-2">
        {/* Value panel (desktop) */}
        <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden border-r border-[color:var(--border-subtle)] p-12 noise-overlay">
          <div className="absolute inset-0 hero-glow opacity-60" />
          <div className="relative">
            <Link to="/" className="inline-flex items-center gap-2.5"><img src={site.logo} alt="Ascendia Detail" className="h-10 w-10 object-contain" /><span className="font-display text-xl font-semibold">Ascendia <span className="text-gold">Detail</span></span></Link>
            <h2 className="mt-14 font-display text-4xl font-semibold leading-tight">Your private<br /><span className="text-gold">client area.</span></h2>
            <p className="mt-4 max-w-sm text-secondary">We treat your home like a private residence, not a job ticket. Your account keeps every detail in order.</p>
            <ul className="mt-10 space-y-6 max-w-sm">
              {PERKS.map((p, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gold/10 border border-[color:var(--border-subtle)]"><p.icon className="h-5 w-5 text-gold-soft" /></span>
                  <div><p className="font-medium text-white">{p.title}</p><p className="text-sm text-muted-c">{p.text}</p></div>
                </li>
              ))}
            </ul>
          </div>
          <p className="relative flex items-center gap-2 text-xs text-muted-c"><ShieldCheck className="h-4 w-4 text-gold-soft" /> Bank-grade security · your data stays private.</p>
        </aside>

        {/* Form panel */}
        <div className="flex items-center justify-center px-4 py-12 sm:py-16">
          <div className="w-full max-w-md">
            <div className="lg:hidden text-center mb-6">
              <Link to="/" className="inline-flex items-center gap-2.5 mb-4"><img src={site.logo} alt="Ascendia Detail" className="h-10 w-10 object-contain" /><span className="font-display text-xl font-semibold">Ascendia <span className="text-gold">Detail</span></span></Link>
            </div>
            <div className="mb-6">
              <h1 className="font-display text-3xl font-semibold text-white">{mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}</h1>
              <p className="text-sm text-muted-c mt-1.5">{mode === "signup" ? "Free to join. Takes under a minute." : mode === "forgot" ? "We'll email you a secure reset link." : "Sign in to your private client area."}</p>
            </div>

            {mode !== "forgot" && (
              <div className="flex rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.03] p-1 mb-5">
                <button onClick={() => setMode("login")} data-testid="account-tab-login" className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-colors ${mode === "login" ? "bg-gold text-black" : "text-white/70 hover:text-white"}`}>Log in</button>
                <button onClick={() => setMode("signup")} data-testid="account-tab-signup" className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-colors ${mode === "signup" ? "bg-gold text-black" : "text-white/70 hover:text-white"}`}>Create account</button>
              </div>
            )}

            {mode !== "forgot" && (
              <div className="mb-5">
                <button type="button" onClick={googleLogin} data-testid="google-signin-button" className="w-full inline-flex items-center justify-center gap-3 rounded-xl bg-white px-6 py-3 text-sm font-medium text-[#1f1f1f] hover:bg-white/90 transition-colors">
                  <GoogleIcon /> Continue with Google
                </button>
                <div className="mt-5 flex items-center gap-3 text-xs text-muted-c">
                  <span className="h-px flex-1 bg-[color:var(--border-subtle)]" /> or continue with email <span className="h-px flex-1 bg-[color:var(--border-subtle)]" />
                </div>
              </div>
            )}

            <form onSubmit={submit} className="ascendia-card card-accent-top p-6 space-y-3">
              {mode === "signup" && (<>
                <div className="grid grid-cols-2 gap-3">
                  <input className={inputCls} placeholder="First name*" value={f.first_name} onChange={upd("first_name")} data-testid="signup-first-name" />
                  <input className={inputCls} placeholder="Last name*" value={f.last_name} onChange={upd("last_name")} data-testid="signup-last-name" />
                </div>
                <input className={inputCls} type="tel" placeholder="Mobile / WhatsApp*" value={f.phone} onChange={upd("phone")} data-testid="signup-phone" />
                <input className={inputCls} placeholder="City (optional)" value={f.city} onChange={upd("city")} />
              </>)}

              {mode === "login" ? (
                <input className={inputCls} placeholder="Email or phone*" value={f.identifier} onChange={upd("identifier")} data-testid="login-identifier" />
              ) : (
                <input className={inputCls} type="email" placeholder="Email*" value={f.email} onChange={upd("email")} data-testid="auth-email" />
              )}

              {mode !== "forgot" && (
                <input className={inputCls} type="password" placeholder={mode === "signup" ? "Password* (min 8, letters + numbers)" : "Password*"} value={f.password} onChange={upd("password")} data-testid="auth-password" />
              )}
              {mode === "signup" && (<>
                <input className={inputCls} type="password" placeholder="Confirm password*" value={f.confirm_password} onChange={upd("confirm_password")} data-testid="signup-confirm" />
                <input className={inputCls} placeholder="Referral code (optional)" value={f.referral_code} onChange={upd("referral_code")} />
              </>)}

              <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50 transition-colors">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "signup" ? "Create my account" : mode === "forgot" ? "Send reset link" : "Log in")}
              </button>

              {mode === "login" && (
                <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-link" className="w-full text-center text-xs text-muted-c hover:text-gold-soft pt-1">Forgot password?</button>
              )}
              {mode === "forgot" && (
                <button type="button" onClick={() => setMode("login")} className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-muted-c hover:text-gold-soft pt-1"><ArrowLeft className="h-3.5 w-3.5" /> Back to log in</button>
              )}
            </form>

            {mode !== "forgot" && (
              <p className="mt-4 text-center text-sm text-muted-c">
                {mode === "login" ? "New to Ascendia? " : "Already have an account? "}
                <button onClick={() => setMode(mode === "login" ? "signup" : "login")} className="text-gold-soft hover:text-gold font-medium">{mode === "login" ? "Create an account" : "Log in"}</button>
              </p>
            )}
            <Link to="/" className="mt-4 block text-center text-sm text-muted-c hover:text-white">← Back to site</Link>
          </div>
        </div>
      </div>
    </>
  );
}
